import React, { useState, useEffect, useMemo } from 'react';
import { buildApiUrl } from '../config';
import { getYearLabel } from '../utils/yearUtils';

const ClassroomSelector = ({ 
  selectedClasses = [], 
  onSelectionChange, 
  assignedForms = [], 
  label = "Select Classes",
  multiSelect = true 
}) => {
  const [allForms, setAllForms] = useState([]);
  const [expandedYears, setExpandedYears] = useState({});
  const [loading, setLoading] = useState(true);

  // Debug logging
  console.log('🏫 ClassroomSelector props:', {
    selectedClasses,
    assignedForms,
    multiSelect,
    onSelectionChange: typeof onSelectionChange
  });

  // Year-form mapping based on backend structure
  const yearFormMap = {
    "Pre-School": ["PS Dolphins", "PS Whale Sharks", "PS Dugongs", "PS Turtles"],
    "Reception": ["R Oryx", "R Sandcats", "R Falcon", "R Camels"],
    "Year 1": ["1A", "1C", "1F", "1O"],
    "Year 2": ["2B", "2F", "2N", "2W"],
    "Year 3": ["3D", "3M", "3S", "3Y"],
    "Year 4": ["4D", "4M", "4W"],
    "Year 5": ["5C", "5F", "5J"],
    "Year 6": ["6A", "6C", "6D"]
  };

  useEffect(() => {
    // Fetch all available forms
    fetch(buildApiUrl("/api/whiteboard/forms"))
      .then(res => res.json())
      .then(forms => {
        setAllForms(Array.isArray(forms) ? forms : []);
        setLoading(false);
      })
      .catch(err => {
        console.error("Failed to fetch all forms:", err);
        setAllForms([]);
        setLoading(false);
      });
    // Fetch students to build dynamic year-form map
    fetch(buildApiUrl('/synced-students'))
      .then(res => res.json())
      .then(data => {
        const arr = Array.isArray(data) ? data : (data?.students || []);
        setAllStudents(arr);
      })
      .catch(err => console.error('Error loading students for ClassroomSelector:', err));
  }, []);

  const toggleYear = (year) => {
    setExpandedYears(prev => ({
      ...prev,
      [year]: !prev[year]
    }));
  };

  const handleFormSelect = (form) => {
    if (typeof onSelectionChange !== 'function') {
      console.error('❌ onSelectionChange is not a function:', typeof onSelectionChange);
      return;
    }
    
    if (multiSelect) {
      const newSelection = selectedClasses.includes(form)
        ? selectedClasses.filter(f => f !== form)
        : [...selectedClasses, form];
      onSelectionChange(newSelection);
    } else {
      onSelectionChange(form);
    }
  };

  const isFormSelected = (form) => {
    return multiSelect ? selectedClasses.includes(form) : selectedClasses === form;
  };

  const getOtherForms = () => {
    // Get forms that are not in teacher's assigned forms
    return allForms.filter(form => !assignedForms.includes(form));
  };

  const getFormsForYear = (forms) => {
    return forms.filter(form => allForms.includes(form));
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-gray-300 rounded w-32 mb-2"></div>
        <div className="h-10 bg-gray-300 rounded"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <label className="block text-lg font-medium mb-2">{label}</label>
      
      <div className="border border-gray-300 rounded-lg max-h-64 overflow-y-auto">
        {/* Teacher's Assigned Forms */}
        {assignedForms.length > 0 && (
          <div className="border-b border-gray-200">
            <div className="p-3 bg-blue-50">
              <h4 className="font-semibold text-blue-900">Your Assigned Classes</h4>
            </div>
            <div className="p-2 space-y-1">
              {assignedForms.map(form => (
                <label key={form} className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input
                    type={multiSelect ? "checkbox" : "radio"}
                    name="classroom-selection"
                    checked={isFormSelected(form)}
                    onChange={() => handleFormSelect(form)}
                    className="mr-3 h-4 w-4 text-blue-600"
                  />
                  <span className="font-medium text-blue-800">{form}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Other Classrooms */}
        {getOtherForms().length > 0 && (
          <div>
            <div className="p-3 bg-gray-50">
              <h4 className="font-semibold text-gray-900">Other Classrooms</h4>
            </div>
            <div className="p-2">
              {Object.entries(yearFormMap).map(([year, forms]) => {
                const availableForms = getFormsForYear(forms).filter(form => 
                  getOtherForms().includes(form)
                );
                
                if (availableForms.length === 0) return null;

                return (
                  <div key={year} className="mb-2">
                    <button
                      onClick={() => toggleYear(year)}
                      className="flex items-center w-full p-2 text-left hover:bg-gray-100 rounded"
                    >
                      <svg
                        className={`w-4 h-4 mr-2 transform transition-transform ${
                          expandedYears[year] ? 'rotate-90' : ''
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium">{year}</span>
                      <span className="ml-auto text-sm text-gray-500">
                        ({availableForms.length})
                      </span>
                    </button>
                    
                    {expandedYears[year] && (
                      <div className="ml-6 space-y-1">
                        {availableForms.map(form => (
                          <label key={form} className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer">
                            <input
                              type={multiSelect ? "checkbox" : "radio"}
                              name="classroom-selection"
                              checked={isFormSelected(form)}
                              onChange={() => handleFormSelect(form)}
                              className="mr-3 h-4 w-4 text-blue-600"
                            />
                            <span>{form}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Selection Summary */}
      {multiSelect && selectedClasses.length > 0 && (
        <div className="p-3 bg-blue-50 rounded-lg">
          <p className="text-sm font-medium text-blue-900">
            Selected: {selectedClasses.length} class{selectedClasses.length !== 1 ? 'es' : ''}
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            {selectedClasses.map(form => (
              <span
                key={form}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
              >
                {form}
                <button
                  onClick={() => handleFormSelect(form)}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassroomSelector;
